/**
 * Health check endpoint for monitoring and diagnostics
 * Accessible at /health
 * Returns JSON with system status (safe, no secrets exposed)
 */

import { useEffect, useState } from 'react';
import { getEnv, getEnvDiagnostics, isEnvValid } from '@/config/env';
import { supabase } from '@/integrations/supabase/client';

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  env: {
    valid: boolean;
    diagnostics: ReturnType<typeof getEnvDiagnostics>;
  };
  supabase: {
    reachable: boolean;
    error?: string;
  };
}

export default function Health() {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkHealth = async () => {
      const status: HealthStatus = {
        status: 'healthy',
        timestamp: new Date().toISOString(),
        env: {
          valid: isEnvValid(),
          diagnostics: getEnvDiagnostics(),
        },
        supabase: {
          reachable: false,
        },
      };

      // Check Supabase connectivity (basic ping)
      try {
        const env = getEnv();
        // Simple connectivity check - try to get auth session (lightweight)
        const { error } = await supabase.auth.getSession();
        status.supabase.reachable = !error || error.message.includes('session'); // Session errors are OK, connection errors are not
        if (error && !error.message.includes('session')) {
          status.supabase.error = error.message;
          status.status = 'degraded';
        }
      } catch (error: any) {
        status.supabase.reachable = false;
        status.supabase.error = error?.message || 'Connection failed';
        status.status = 'unhealthy';
      }

      // Determine overall status
      if (!status.env.valid) {
        status.status = 'unhealthy';
      } else if (!status.supabase.reachable) {
        status.status = 'degraded';
      }

      setHealth(status);
      setLoading(false);
    };

    checkHealth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">Checking system health...</p>
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive">Health check failed</p>
        </div>
      </div>
    );
  }

  // Return JSON for API consumers, or render UI for browser
  const isApiRequest = typeof window !== 'undefined' && 
    (window.location.search.includes('format=json') || 
     window.navigator.userAgent.includes('curl') ||
     window.navigator.userAgent.includes('httpie'));

  if (isApiRequest) {
    // Return JSON response
    return (
      <pre className="min-h-screen p-8 bg-background font-mono text-sm">
        {JSON.stringify(health, null, 2)}
      </pre>
    );
  }

  // Render UI
  const statusColor = 
    health.status === 'healthy' ? 'text-green-600' :
    health.status === 'degraded' ? 'text-yellow-600' :
    'text-red-600';

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">System Health</h1>
          <p className="text-muted-foreground">
            Status: <span className={`font-semibold ${statusColor}`}>{health.status.toUpperCase()}</span>
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Checked at: {new Date(health.timestamp).toLocaleString()}
          </p>
        </div>

        <div className="bg-muted p-4 rounded-lg space-y-4">
          <div>
            <h2 className="font-semibold mb-2">Environment Variables</h2>
            <ul className="space-y-1 text-sm">
              <li>Valid: {health.env.valid ? '✓' : '✗'}</li>
              <li>VITE_SUPABASE_URL: {health.env.diagnostics.hasSupabaseUrl ? '✓' : '✗'}</li>
              <li>VITE_SUPABASE_ANON_KEY: {health.env.diagnostics.hasSupabaseKey ? '✓' : '✗'}</li>
              <li>VITE_SITE_URL: {health.env.diagnostics.hasSiteUrl ? '✓ (optional)' : 'Not set (optional)'}</li>
              <li>VITE_STRIPE_PRICE_ID: {health.env.diagnostics.hasStripePriceId ? '✓ (optional)' : 'Not set (optional)'}</li>
            </ul>
          </div>

          <div>
            <h2 className="font-semibold mb-2">Supabase</h2>
            <ul className="space-y-1 text-sm">
              <li>Reachable: {health.supabase.reachable ? '✓' : '✗'}</li>
              {health.supabase.error && (
                <li className="text-destructive">Error: {health.supabase.error}</li>
              )}
            </ul>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          <p>For JSON format, append <code className="bg-muted px-1 rounded">?format=json</code> to the URL</p>
        </div>
      </div>
    </div>
  );
}

